"use client"

import { useAudioPlayer } from "./audio-player-provider"
import { Button } from "./ui/button"
import { Play, Pause, X, Maximize2 } from "lucide-react"
import { useState, useEffect, useRef } from "react"

const formatTime = (time: number) => {
  if (!Number.isFinite(time)) return "0:00"
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export function MiniAudioPlayer() {
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    togglePlayPause,
    stop,
    openExpanded,
    seek,
    isExpanded,
  } = useAudioPlayer()

  // Load saved position from localStorage
  const loadSavedPosition = () => {
    if (typeof window === "undefined") return { x: 0, y: 0 }
    const saved = localStorage.getItem("miniAudioPlayerPosition")
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return { x: 0, y: 0 }
      }
    }
    return { x: 0, y: 0 }
  }

  const [position, setPosition] = useState(loadSavedPosition)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const playerRef = useRef<HTMLDivElement>(null)

  // Save position to localStorage when it changes (but not during dragging)
  useEffect(() => {
    if (!isDragging && (position.x !== 0 || position.y !== 0)) {
      localStorage.setItem("miniAudioPlayerPosition", JSON.stringify(position))
    }
  }, [position, isDragging])

  // Handle mouse drag (only on the container, not buttons)
  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if clicking on a button or interactive element
    if (
      (e.target as HTMLElement).closest("button") ||
      (e.target as HTMLElement).closest("[role='button']")
    ) {
      return
    }
    
    if (!playerRef.current) return
    const rect = playerRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
    setIsDragging(true)
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y
      
      // Constrain to viewport
      const maxX = window.innerWidth - (playerRef.current?.offsetWidth || 320)
      const maxY = window.innerHeight - (playerRef.current?.offsetHeight || 80)
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, dragOffset])

  // Don't show if no track or if expanded modal is open
  if (!currentTrack || isExpanded) {
    return null
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  // Determine positioning - if position is default (0,0), use bottom-right corner
  const isDefaultPosition = position.x === 0 && position.y === 0
  const positionStyle: React.CSSProperties = isDefaultPosition
    ? {
        bottom: "1.5rem",
        right: "1.5rem",
      }
    : {
        left: `${position.x}px`,
        top: `${position.y}px`,
      }

  return (
    <div
      ref={playerRef}
      className={`fixed z-[100] bg-background border-2 border-primary/20 rounded-lg shadow-2xl transition-all duration-200 backdrop-blur-sm ${
        isDragging ? "cursor-grabbing scale-105" : "cursor-grab"
      }`}
      style={{
        ...positionStyle,
        width: "320px",
        minWidth: "320px",
        opacity: 1,
        display: "block",
        visibility: "visible",
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="p-3 space-y-2">
        {/* Header with title and controls */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">Now Playing</p>
            <p className="text-sm font-medium truncate" title={currentTrack.title}>
              {currentTrack.title}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={openExpanded}
              title="Expand player"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={stop}
              title="Stop"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-1">
          <div
            className="h-1.5 rounded-full bg-muted cursor-pointer relative"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const percent = (e.clientX - rect.left) / rect.width
              const newTime = percent * duration
              seek(newTime)
            }}
          >
            <div
              className="absolute inset-y-0 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Play/Pause button */}
        <div className="flex items-center justify-center">
          <Button
            size="sm"
            className="rounded-full h-9 w-9"
            onClick={togglePlayPause}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4 ml-0.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

